import { WaypointResponse } from '@/shared/api/routes'
import { generateTripDays, type TripDay } from '@/modules/meals/utils/generateTripDays'

export interface ItineraryDay {
  day: number
  date: string
  dateRange?: string // For multi-day ranges like "Friday, Nov 28 - Sunday, Nov 30"
  waypoints: WaypointResponse[]
  totalDistance: number // in miles
  totalDuration: number // in minutes
  waypointCount: number
}

/**
 * Organize waypoints by trip day and show all days from start_date to end_date
 */
export function organizeWaypointsByDay(
  waypoints: WaypointResponse[],
  tripStartDate: string | null,
  tripEndDate: string | null
): ItineraryDay[] {
  // Group waypoints by trip_day
  const waypointsByDay = new Map<number, WaypointResponse[]>()

  waypoints.forEach(waypoint => {
    // trip_day defaults to 1 if not specified
    const day = waypoint.trip_day ?? 1
    if (!waypointsByDay.has(day)) {
      waypointsByDay.set(day, [])
    }
    waypointsByDay.get(day)!.push(waypoint)
  })

  // Generate all trip days if we have dates
  let allTripDays: TripDay[] = []
  let totalDays = 0

  // Calculate max day from waypoints to ensure all waypoints are displayed
  const maxWaypointDay = waypoints.length > 0 
    ? Math.max(...Array.from(waypointsByDay.keys()), 0)
    : 0
  const maxContentDay = Math.max(maxWaypointDay, 1)

  if (tripStartDate && tripEndDate) {
    allTripDays = generateTripDays(tripStartDate, tripEndDate)
    const dateRangeDays = allTripDays.length
    // Use the maximum of date range days and content days to ensure all waypoints are displayed
    // This handles cases where waypoints have trip_day values beyond the trip's date range
    totalDays = Math.max(dateRangeDays, maxContentDay)
  } else if (tripStartDate) {
    // If only start date, use max day from waypoints/items or default to 1
    totalDays = maxContentDay
  } else {
    // If no dates, use max day from waypoints/items or default to 1
    totalDays = maxContentDay
  }

  // Create ItineraryDay for each day (1 to totalDays)
  const itineraryDays: ItineraryDay[] = []

  for (let day = 1; day <= totalDays; day++) {
    const dayWaypoints = waypointsByDay.get(day) || []
    
    // Calculate date for this day
    let date = ''
    let dateRange = ''
    
    if (tripStartDate && allTripDays.length > 0) {
      // Use the generated trip day
      const tripDay = allTripDays[day - 1]
      if (tripDay) {
        date = tripDay.formattedDate
        dateRange = date
      } else {
        // Fallback if day is beyond trip days
        const startDate = new Date(tripStartDate)
        const dayDate = new Date(startDate)
        dayDate.setDate(startDate.getDate() + (day - 1))
        date = formatDate(dayDate)
        dateRange = date
      }
    } else {
      date = `Day ${day}`
      dateRange = `Day ${day}`
    }

    // Stats will come from backend route data or stats endpoint
    // For now, set defaults (will be updated when routes are fetched)
    // Sort waypoints by order_index first (ascending), then by start_time if available
    // This ensures stable ordering - waypoints maintain position when times are added
    const sortedWaypoints = [...dayWaypoints].sort((a, b) => {
      // Primary sort: order_index (ascending)
      const orderA = a.order_index ?? 999999
      const orderB = b.order_index ?? 999999
      if (orderA !== orderB) {
        return orderA - orderB
      }
      
      // Secondary sort: start_time if both have it (ascending)
      // Only use time as tiebreaker, not primary sort
      if (a.start_time && b.start_time) {
        const timeA = parseTimeToMinutes(a.start_time)
        const timeB = parseTimeToMinutes(b.start_time)
        if (timeA !== null && timeB !== null && timeA !== timeB) {
          return timeA - timeB
        }
      }
      
      // Tertiary sort: preserve original order (by id as stable tiebreaker)
      return a.id.localeCompare(b.id)
    })

    itineraryDays.push({
      day,
      date,
      dateRange,
      waypoints: sortedWaypoints,
      totalDistance: 0, // Will be populated from route data
      totalDuration: 0, // Will be populated from route data
      waypointCount: sortedWaypoints.length
    })
  }

  return itineraryDays
}

/**
 * @deprecated calculateDayStats removed - use backend stats endpoint instead
 * Stats are now calculated from actual route data on the backend
 */

/**
 * Format date as "Day, Month DD" (e.g., "Thursday, Nov 27")
 */
function formatDate(date: Date): string {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ]

  const dayName = days[date.getDay()]
  const month = months[date.getMonth()]
  const day = date.getDate()

  return `${dayName}, ${month} ${day}`
}

/**
 * Parse time string (HH:MM or HHMM format) to minutes since midnight
 * Used for stable sorting by time
 */
function parseTimeToMinutes(timeString: string | null | undefined): number | null {
  if (!timeString) return null
  
  // Handle both HH:MM and HHMM formats
  const normalized = timeString.replace(':', '')
  if (normalized.length !== 4) return null

  const hours = parseInt(normalized.substring(0, 2), 10)
  const minutes = parseInt(normalized.substring(2, 4), 10)

  if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null
  }

  return hours * 60 + minutes
}

